import React from "react";
import {
  Menu,
  MenuItem,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import SouthIcon from "@mui/icons-material/South";
import NorthIcon from "@mui/icons-material/North";
import Cell from "src/Tables/TableTemplate/Cell";
import { ITableUI } from "src/Tables/TableTemplate/tableTemplate";
import { Header, ITableReport } from "src/Interfaces/TableInterfaces";
import PortfoSegregationModal from "./PortfoSegregationModal";

const headerCellClassName =
  "!cursor-default bg-white !font-black text-black !text-center";

const sortableHeaderCellClassName =
  "!cursor-pointer bg-white hover:!bg-slate-200 !transition-all !font-black text-black !text-center";

const tableCellClassName =
  "whitespace-nowrap overflow-hidden text-ellipsis";

const dynamicCellClassName = "!w-[120px] !min-w-[120px] !max-w-[120px]";
const expandedValueCellClassName = "!w-[140px] !min-w-[140px] !max-w-[140px]";
const compactPercentCellClassName = "!w-[100px] !min-w-[100px] !max-w-[100px]";
const compactStaticCellClassName =
  "!px-2 whitespace-nowrap overflow-hidden text-ellipsis";

const getGroupBorderClassName = (headers: Header[], index: number) => {
  const isParentGroupStart =
    index === 0 ||
    headers[index - 1].parentGroupName !== headers[index].parentGroupName;
  const isChildGroupStart =
    !isParentGroupStart &&
    headers[index - 1].groupName !== headers[index].groupName;
  const isLastColumn = index === headers.length - 1;

  return `${isParentGroupStart ? "!border-r-4 !border-r-sky-200" : ""} ${
    isChildGroupStart ? "!border-r-2 !border-r-sky-200" : ""
  } ${
    isLastColumn ? "!border-l-4 !border-l-sky-200" : ""
  }`;
};

type DynamicHeaderGroup = {
  groupName: string;
  parentGroupName?: string | null;
  headers: Header[];
};

const groupDynamicHeaders = (headers: Header[]) => {
  const groups: DynamicHeaderGroup[] = [];

  headers.forEach((header) => {
    const lastGroup = groups[groups.length - 1];
    if (
      lastGroup &&
      lastGroup.groupName === header.groupName &&
      lastGroup.parentGroupName === header.parentGroupName
    ) {
      lastGroup.headers.push(header);
      return;
    }

    groups.push({
      groupName: header.groupName!,
      parentGroupName: header.parentGroupName,
      headers: [header],
    });
  });

  return groups;
};

const groupParentHeaders = (groups: DynamicHeaderGroup[]) => {
  const parentGroups: { groupName: string; groups: DynamicHeaderGroup[] }[] = [];

  groups.forEach((group) => {
    const groupName = group.parentGroupName ?? group.groupName;
    const lastParentGroup = parentGroups[parentGroups.length - 1];
    if (lastParentGroup && lastParentGroup.groupName === groupName) {
      lastParentGroup.groups.push(group);
      return;
    }

    parentGroups.push({ groupName, groups: [group] });
  });

  return parentGroups;
};

const getDynamicGroupBorderClassName = (
  groups: DynamicHeaderGroup[],
  index: number,
) => {
  const isParentGroupStart =
    index === 0 ||
    groups[index - 1].parentGroupName !== groups[index].parentGroupName;
  const isLastGroup = index === groups.length - 1;

  return `${isParentGroupStart ? "!border-r-4 !border-r-sky-200" : "!border-r-2 !border-r-sky-200"} ${
    isLastGroup ? "!border-l-4 !border-l-sky-200" : ""
  }`;
};

const filterDynamicHeaders = (headers: Header[], showHiddenCols?: boolean) => {
  if (showHiddenCols == null) return headers;

  return headers.filter((header) =>
    showHiddenCols
      ? header.positionTypeStr === "FirstPage" ||
        header.positionTypeStr === "DefaultHidden"
      : header.positionTypeStr === "FirstPage",
  );
};

const getFrozenStaticHeaders = (headers: Header[]) => {
  const staticHeaders = headers.filter((header) => !header.groupName);
  const markedFixedHeaders = staticHeaders.filter(
    (header) => header.positionTypeStr === "FixedFirst",
  );

  return (markedFixedHeaders.length >= 2
    ? markedFixedHeaders
    : staticHeaders
  ).slice(0, 2);
};

const getStaticCellWidthClassName = (
  header: Header,
  autoSizeAccountColumns?: boolean,
) => {
  const headerName = header.headerName.toLowerCase();

  if (headerName === "row") return "!w-10 !min-w-10 !max-w-10";
  if (headerName.endsWith("accountname"))
    return autoSizeAccountColumns
      ? ""
      : "!w-[140px] !min-w-[140px] !max-w-[140px]";
  if (headerName.endsWith("connected"))
    return "!w-14 !min-w-14 !max-w-14";
  if (headerName.endsWith("portfogroupname"))
    return "!w-[110px] !min-w-[110px] !max-w-[110px]";

  return "";
};

const renderHeaderText = (
  text?: string | null,
  sameSizeMultilineHeaders?: boolean,
) => {
  const lines = text?.split("\n") ?? [];
  if (lines.length <= 1) return <span>{text}</span>;

  return (
      <span className="leading-5">
      <span className="block">{lines[0]}</span>
      {lines.slice(1).map((line, index) => (
        <span
          key={`${line}-${index}`}
          className={
            sameSizeMultilineHeaders
              ? "block leading-5"
              : "block !font-normal text-[10px] leading-4"
          }
        >
          {line}
        </span>
      ))}
    </span>
  );
};

const AllocationTableUI = <TRow extends ITableReport>({
  reports,
  onAction,
  tableBoxClassName,
  sortingType,
  setSortingType,
  onTableHeaderClick,
  onTableRowClick,
  isRowClickable,
  showHiddenCols,
  compactPercentAndExpandValue,
  autoSizeAccountColumns,
  sameSizeMultilineHeaders,
  activeRowId,
}: ITableUI<TRow>) => {
  const firstHeaderRowRef = React.useRef<HTMLTableRowElement>(null);
  const secondHeaderRowRef = React.useRef<HTMLTableRowElement>(null);
  const fixedFirstHeaderCellRefs = React.useRef<
    Record<string, HTMLTableCellElement | null>
  >({});
  const [firstRowHeight, setFirstRowHeight] = React.useState(0);
  const [secondRowHeight, setSecondRowHeight] = React.useState(0);
  const [fixedFirstRightOffsets, setFixedFirstRightOffsets] = React.useState<
    Record<string, number>
  >({});
  const [segregationModal, setSegregationModal] = React.useState<{
    accountId: number;
    tag: string;
  } | null>(null);
  const [contextMenuAnchor, setContextMenuAnchor] =
    React.useState<HTMLElement | null>(null);
  const [contextMenuData, setContextMenuData] = React.useState<{
    accountId: number;
    tag: string;
  } | null>(null);
  const [selectedRowId, setSelectedRowId] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    const row = firstHeaderRowRef.current;
    if (!row) return;

    const updateHeight = () => {
      setFirstRowHeight(row.getBoundingClientRect().height);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(row);
    return () => observer.disconnect();
  }, [reports]);

  React.useLayoutEffect(() => {
    const row = secondHeaderRowRef.current;
    if (!row) {
      setSecondRowHeight(0);
      return;
    }

    const updateHeight = () => {
      setSecondRowHeight(row.getBoundingClientRect().height);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(row);
    return () => observer.disconnect();
  }, [reports]);

  React.useLayoutEffect(() => {
    if (!reports) return;

    const updateOffsets = () => {
      const fixedFirst = getFrozenStaticHeaders(
        reports.translatedHeaders ?? [],
      );
      const offsets: Record<string, number> = {};
      let accumulatedRight = 0;

      fixedFirst.forEach((header) => {
        offsets[header.headerName] = accumulatedRight;
        const cell = fixedFirstHeaderCellRefs.current[header.headerName];
        accumulatedRight += cell?.getBoundingClientRect().width ?? 0;
      });

      setFixedFirstRightOffsets(offsets);
    };

    updateOffsets();
    const row = firstHeaderRowRef.current;
    if (!row) return;

    const observer = new ResizeObserver(updateOffsets);
    observer.observe(row);
    return () => observer.disconnect();
  }, [reports]);

  if (!reports) return null;

  const highlightedRowId = activeRowId ?? selectedRowId;

  const rowClickHandler = (row: TRow) => {
    if (isRowClickable && !isRowClickable(row)) return;
    setSelectedRowId(row.id);
    onTableRowClick?.(row);
  };

  const headers = reports.translatedHeaders ?? [];
  const staticHeaders = headers.filter((header) => !header.groupName);
  const fixedFirstHeaders = getFrozenStaticHeaders(headers);
  const fixedFirstHeaderNames = new Set(
    fixedFirstHeaders.map((header) => header.headerName),
  );
  const scrollableStaticHeaders = staticHeaders.filter(
    (header) => !fixedFirstHeaderNames.has(header.headerName),
  );
  const dynamicHeaders = filterDynamicHeaders(
    headers.filter((header) => header.groupName),
    showHiddenCols,
  );
  const dynamicGroups = groupDynamicHeaders(dynamicHeaders);
  const hasParentGroups = dynamicHeaders.some(
    (header) => header.parentGroupName != null,
  );
  const parentGroups = hasParentGroups ? groupParentHeaders(dynamicGroups) : [];
  const dynamicHeaderRowSpan = dynamicHeaders.length
    ? hasParentGroups
      ? 3
      : 2
    : undefined;

  const getFixedFirstInnerCellClassName = (header: Header) =>
    header.translateHeaderName === "#" ||
    (autoSizeAccountColumns && header.headerName.toLowerCase().endsWith("accountname"))
      ? ""
      : "inner-fixed-column";

  const getFixedFirstRightOffset = (header: Header) =>
    fixedFirstRightOffsets[header.headerName] ?? 0;

  const getDynamicCellWidthClassName = (header: Header) => {
    if (!compactPercentAndExpandValue) return dynamicCellClassName;
    if (
      header.headerName.endsWith("_value") ||
      header.headerName.endsWith("_requiredMargin") ||
      header.headerName.endsWith("_engagedCapital")
    )
      return expandedValueCellClassName;
    if (header.headerName.endsWith("_percent")) return compactPercentCellClassName;
    return dynamicCellClassName;
  };

  const tableHeaderClickHandler = (header: Header) => {
    if ((sortingType || sortingType === "") && setSortingType)
      setSortingType(header.headerName);
    if (onTableHeaderClick) onTableHeaderClick(header);
  };

  const handleCloseContextMenu = () => {
    setContextMenuAnchor(null);
    setContextMenuData(null);
  };

  const dynamicCellRightClickHandler = (
    e: React.MouseEvent<HTMLElement>,
    row: TRow,
    header: Header,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const tag = header.tag != null && header.tag !== "" ? header.tag : null;
    if (tag == null || Number.isNaN(tag)) return;
    setContextMenuAnchor(e.currentTarget);
    setContextMenuData({ accountId: row.id, tag: tag as string });
  };

  const detailsClickHandler = () => {
    if (contextMenuData) setSegregationModal(contextMenuData);
    handleCloseContextMenu();
  };

  const getHeadersSortingIcon = (theader: Header) => {
    if (!sortingType) return null;

    const sortingTypeParts = sortingType.split("_");
    const sortingTypeHeaderName = sortingTypeParts.slice(0, -1).join("_");
    if (theader.headerName === sortingTypeHeaderName) {
      if (sortingTypeParts[sortingTypeParts.length - 1] === "desc")
        return <SouthIcon className="!text-xs" />;
      if (sortingTypeParts[sortingTypeParts.length - 1] === "asc")
        return <NorthIcon className="!text-xs" />;
    }
  };

  const getTableHeaderJSX = (theader: Header, className?: string) => (
    <TableCell
      key={theader.headerName}
      rowSpan={dynamicHeaderRowSpan}
      style={{ top: 0 }}
      className={`${sortableHeaderCellClassName} !sticky !z-30 ${theader.classname ?? ""} ${className ?? ""}`}
      onClick={() => tableHeaderClickHandler(theader)}
    >
      {renderHeaderText(theader.translateHeaderName, sameSizeMultilineHeaders)}
      {getHeadersSortingIcon(theader)}
    </TableCell>
  );

  const renderHeaderRows = () => (
    <>
      <TableRow ref={firstHeaderRowRef}>
        {fixedFirstHeaders.map((header) => (
          <TableCell
            key={header.headerName}
            ref={(el) => {
              fixedFirstHeaderCellRefs.current[header.headerName] =
                el as HTMLTableCellElement | null;
            }}
            rowSpan={dynamicHeaderRowSpan}
            style={{ right: getFixedFirstRightOffset(header), top: 0 }}
            className={`${sortableHeaderCellClassName} !sticky !z-[100] !bg-sky-50 fixed-column ${compactStaticCellClassName} ${getStaticCellWidthClassName(header, autoSizeAccountColumns)} ${getFixedFirstInnerCellClassName(header)} ${header.classname ?? ""}`}
            onClick={() => tableHeaderClickHandler(header)}
          >
            {renderHeaderText(header.translateHeaderName, sameSizeMultilineHeaders)}
            {getHeadersSortingIcon(header)}
          </TableCell>
        ))}
        {scrollableStaticHeaders.map((header) =>
          getTableHeaderJSX(
            header,
            `${compactStaticCellClassName} ${getStaticCellWidthClassName(header, autoSizeAccountColumns)}`,
          ),
        )}
        {(hasParentGroups ? parentGroups : dynamicGroups).map((group, index) => (
          <TableCell
            key={group.groupName}
            colSpan={
              "groups" in group
                ? group.groups.reduce(
                    (columnCount, childGroup) =>
                      columnCount + childGroup.headers.length,
                    0,
                  )
                : group.headers.length
            }
            style={{ top: 0 }}
            className={`${headerCellClassName} !sticky !z-30 !border-y-2 !border-y-sky-200 ${hasParentGroups ? "!border-r-4 !border-r-sky-200" : "!border-r-2 !border-r-sky-200"} ${index === (hasParentGroups ? parentGroups.length : dynamicGroups.length) - 1 ? hasParentGroups ? "!border-l-4 !border-l-sky-200" : "!border-l-2 !border-l-sky-200" : ""}`}
          >
            {renderHeaderText(group.groupName, sameSizeMultilineHeaders)}
          </TableCell>
        ))}
      </TableRow>
      {dynamicHeaders.length ? (
        hasParentGroups ? (
          <>
            <TableRow ref={secondHeaderRowRef}>
              {dynamicGroups.map((group, index) => (
                <TableCell
                  key={`${group.parentGroupName}-${group.groupName}`}
                  colSpan={group.headers.length}
                  style={{ top: firstRowHeight }}
                  className={`${headerCellClassName} !sticky !z-20 !border-y-2 !border-y-sky-200 ${getDynamicGroupBorderClassName(dynamicGroups, index)}`}
                >
                  {renderHeaderText(group.groupName, sameSizeMultilineHeaders)}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {dynamicHeaders.map((header, index) => (
                <TableCell
                  key={header.headerName}
                  style={{ top: firstRowHeight + secondRowHeight }}
                  className={`${sortableHeaderCellClassName} ${getDynamicCellWidthClassName(header)} !border-b-2 !border-b-sky-200 ${header.classname ?? ""} ${getGroupBorderClassName(dynamicHeaders, index)}`}
                  onClick={() => tableHeaderClickHandler(header)}
                >
                  {renderHeaderText(header.translateHeaderName, sameSizeMultilineHeaders)}
                  {getHeadersSortingIcon(header)}
                </TableCell>
              ))}
            </TableRow>
          </>
        ) : (
          <TableRow>
            {dynamicHeaders.map((header, index) => (
              <TableCell
                key={header.headerName}
                style={{ top: firstRowHeight }}
                className={`${sortableHeaderCellClassName} ${getDynamicCellWidthClassName(header)} !border-b-2 !border-b-sky-200 ${header.classname ?? ""} ${getGroupBorderClassName(dynamicHeaders, index)}`}
                onClick={() => tableHeaderClickHandler(header)}
              >
                {renderHeaderText(header.translateHeaderName, sameSizeMultilineHeaders)}
                {getHeadersSortingIcon(header)}
              </TableCell>
            ))}
          </TableRow>
        )
      ) : null}
    </>
  );

  const renderBody = () => {
    if (!headers.length || !reports.report.length) {
      return (
        <TableRow className="!relative h-10 !w-full">
          <TableCell className="!w-full !m-auto !flex !justify-center !items-center !h-full !absolute">
            موردی یافت نشد
          </TableCell>
        </TableRow>
      );
    }

    return reports.report.map((row) => {
      const clonedRow = JSON.parse(JSON.stringify(row));
      const isActiveRow = highlightedRowId === row.id;
      const isClickableRow = !isRowClickable || isRowClickable(row);

      return (
        <TableRow
          key={row.row}
          className={`group portfolio-allocation-row !transition-all !w-full !relative ${
            isActiveRow ? "!bg-blue-200" : "hover:!bg-slate-100"
          } ${isClickableRow ? "cursor-pointer" : "cursor-default"}`}
          onClick={isClickableRow ? () => rowClickHandler(row) : undefined}
        >
          {fixedFirstHeaders.map((header) => (
            <TableCell
              key={`${header.headerName}-${row.row}`}
              style={{ right: getFixedFirstRightOffset(header) }}
              className={`!sticky !z-50 ${isActiveRow ? "!bg-blue-200" : "!bg-sky-50 group-hover:!bg-slate-100"} fixed-column ${compactStaticCellClassName} ${getStaticCellWidthClassName(header, autoSizeAccountColumns)} ${getFixedFirstInnerCellClassName(header)}`}
            >
              <div className="flex flex-row justify-center items-center gap-2 !w-full min-w-0">
                <Cell
                  onAction={onAction}
                  clonedRow={clonedRow}
                  headerName={header.headerName}
                  type={header.typeStr}
                />
              </div>
            </TableCell>
          ))}
          {scrollableStaticHeaders.map((header) => (
            <TableCell
              key={`${header.headerName}-${row.row}`}
              className={`${compactStaticCellClassName} ${getStaticCellWidthClassName(header, autoSizeAccountColumns)}`}
            >
              <div className="flex flex-row justify-center items-center gap-2 !w-full min-w-0">
                <Cell
                  onAction={onAction}
                  clonedRow={clonedRow}
                  headerName={header.headerName}
                  type={header.typeStr}
                />
              </div>
            </TableCell>
          ))}
          {dynamicHeaders.map((header, index) => (
            <TableCell
              key={`${header.headerName}-${row.row}`}
              className={`${tableCellClassName} ${getDynamicCellWidthClassName(header)} ${getGroupBorderClassName(dynamicHeaders, index)}`}
            >
              <div
                className="flex flex-row justify-center items-center gap-2 !w-full"
                onContextMenu={(e) =>
                  dynamicCellRightClickHandler(e, row, header)
                }
              >
                <Cell
                  onAction={onAction}
                  clonedRow={clonedRow}
                  headerName={header.headerName}
                  type={header.typeStr}
                />
              </div>
            </TableCell>
          ))}
        </TableRow>
      );
    });
  };

  const multiRowStickyHeaderSx = {
    "& .MuiTableCell-head": {
      backgroundColor: "#fff",
      position: "sticky",
    },
    "& thead tr:first-of-type .MuiTableCell-head": {
      top: 0,
      zIndex: 30,
    },
    "& thead tr:nth-of-type(2) .MuiTableCell-head": {
      top: firstRowHeight,
      zIndex: 20,
    },
    "& thead tr:nth-of-type(3) .MuiTableCell-head": {
      top: firstRowHeight + secondRowHeight,
      zIndex: 10,
    },
  };

  return (
    <>
      <Menu
        anchorEl={contextMenuAnchor}
        open={Boolean(contextMenuAnchor)}
        onClose={handleCloseContextMenu}
      >
        <MenuItem onClick={detailsClickHandler}>جزئیات</MenuItem>
      </Menu>
      {segregationModal ? (
        <PortfoSegregationModal
          accountId={segregationModal.accountId}
          tag={segregationModal.tag}
          onClose={() => setSegregationModal(null)}
        />
      ) : null}
      <div className={`w-full overflow-auto ${tableBoxClassName ?? ""}`}>
        <div className="portfolio-allocation-table w-fit mx-auto border-2 border-sky-100 rounded">
          <MuiTable
            stickyHeader
            className="relative min-h-[100px] !w-max"
            sx={multiRowStickyHeaderSx}
          >
            <TableHead>{renderHeaderRows()}</TableHead>
            <TableBody>{renderBody()}</TableBody>
          </MuiTable>
        </div>
      </div>
    </>
  );
};

export default AllocationTableUI;
